import { z } from 'zod';
import { dateKeySchema, monthSchema } from '../money';
import { objectIdSchema, optionalText, paginationQuerySchema, patchOf } from './common';

/** Leave is counted in half-day units (1 day = 2 units) so half days never need fractions. */
export const LEAVE_STATUSES = ['pending', 'approved', 'rejected', 'cancelled'] as const;
export type LeaveStatus = (typeof LEAVE_STATUSES)[number];
export const HALF_DAY_OPTIONS = ['none', 'first_half', 'second_half'] as const;

export const leaveTypeSchema = z.object({
  name: z.string().trim().min(2).max(60),
  code: z.string().trim().toUpperCase().regex(/^[A-Z0-9_]{1,10}$/, 'Use up to 10 letters, digits or _'),
  /** Paid leave does not reduce salary; unpaid leave is loss of pay in payroll. */
  paid: z.boolean().default(true),
  /** Days allowed per calendar year (0 = no fixed quota, e.g. unpaid leave). Half days allowed. */
  annualQuotaDays: z.coerce.number().min(0).max(366).multipleOf(0.5).default(0),
  requiresApproval: z.boolean().default(true),
  active: z.boolean().default(true),
});
export type LeaveTypeInput = z.input<typeof leaveTypeSchema>;
export const leaveTypeUpdateSchema = patchOf(leaveTypeSchema);

export const holidaySchema = z.object({
  date: dateKeySchema,
  name: z.string().trim().min(2).max(80),
  optional: z.boolean().default(false),
});

export const leaveApplySchema = z
  .object({
    leaveTypeId: objectIdSchema,
    fromDate: dateKeySchema,
    toDate: dateKeySchema,
    halfDay: z.enum(HALF_DAY_OPTIONS).default('none'),
    reason: z.string().trim().min(3).max(1000),
  })
  .refine((v) => v.toDate >= v.fromDate, { message: 'End date is before the start date', path: ['toDate'] })
  .refine((v) => v.halfDay === 'none' || v.fromDate === v.toDate, { message: 'A half day must be a single date', path: ['halfDay'] });
export type LeaveApplyInput = z.input<typeof leaveApplySchema>;

export const leaveDecisionSchema = z.object({ decision: z.enum(['approve', 'reject']), note: optionalText(500) });
export const leaveCancelSchema = z.object({ reason: optionalText(500) });

export const leaveListQuerySchema = paginationQuerySchema.extend({
  status: z.enum(LEAVE_STATUSES).optional(),
  userId: objectIdSchema.optional(),
  month: monthSchema.optional(),
  view: z.enum(['mine', 'team']).optional(),
});
export const leaveBalanceQuerySchema = z.object({ userId: objectIdSchema.optional(), year: z.coerce.number().int().min(2000).max(2100).optional() });
export const holidayListQuerySchema = z.object({ year: z.coerce.number().int().min(2000).max(2100).optional() });
